import { EVENT_CATEGORIES, EVENT_TYPES } from '../constants';
import { withEventOverrides, type TEventOverrides } from '../lib/withEventOverrides';
import type { IInitialEvent } from '../types';

// Анкета

const EVENT_CATEGORY = EVENT_CATEGORIES.CREDITS;
const EVENT_TYPE = EVENT_TYPES.MAIN_EVENT;

type TStepDataItem = {
  fieldLabel: string;
  fieldValue: string;
};

const getFillTypeLabel = (fillType: 'auto' | 'manual') => (fillType === 'auto' ? 'Проброс' : 'Ручное');

const getStepDataLabels = (stepData: TStepDataItem[]) =>
  stepData.map((param) => `${param.fieldLabel} - ${param.fieldValue}`);

export const eventFillFormField = (
  step: number,
  fieldName: string,
  isRequired: boolean,
  overrides?: TEventOverrides,
): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Заполнение поля',
      eventLabel: [`Шаг ${step}`, fieldName, isRequired ? 'Обязательное' : 'Необязательное'],
      eventValue: undefined,
    },
    overrides,
  );

export const eventFormFieldError = (
  step: number,
  fieldName: string,
  isRequired: boolean,
  overrides?: TEventOverrides,
): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Ошибка заполнения поля',
      eventLabel: [`Шаг ${step}`, fieldName, 'Ошибка валидации', isRequired ? 'Обязательное' : 'Необязательное'],
      eventValue: undefined,
    },
    overrides,
  );

export const eventInvalidForm = (
  step: number,
  passType: 'auto' | 'manual',
  fieldName: string,
  fieldValue: string,
  overrides?: TEventOverrides,
  // eslint-disable-next-line max-params
): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Невалидная анкета',
      eventLabel: [`Шаг ${step}`, fieldName, passType === 'auto' ? 'Автопроход' : 'Ручной', fieldValue],
      eventValue: undefined,
    },
    overrides,
  );

export const eventChangeFormFieldDefaultParam = (
  step: number,
  parameterName: string,
  overrides?: TEventOverrides,
): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Изменение параметра',
      eventLabel: [`Шаг ${step}`, parameterName],
      eventValue: undefined,
    },
    overrides,
  );

export const eventGetCodeBySms = (overrides?: TEventOverrides): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Получить код по СМС',
      eventLabel: undefined,
      eventValue: undefined,
    },
    overrides,
  );

export const eventInvalidSmsCode = (overrides?: TEventOverrides): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Клик',
      eventLabel: 'Неверный код СМС',
      eventValue: undefined,
    },
    overrides,
  );

export const eventChangePhoneNumber = (overrides?: TEventOverrides): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Клик',
      eventLabel: 'Изменить номер',
      eventValue: undefined,
    },
    overrides,
  );

export const eventRequestNewCode = (overrides?: TEventOverrides): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Клик',
      eventLabel: 'Получить новый код СМС',
      eventValue: undefined,
    },
    overrides,
  );

export const eventOnEsiaButtonClick = (overrides?: TEventOverrides): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Клик',
      eventLabel: 'Баннер есиа',
      eventValue: undefined,
    },
    overrides,
  );

export const eventOnEsiaBannerAuthSuccess = (overrides?: TEventOverrides): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Вход в лк',
      eventLabel: 'Баннер есиа',
      eventValue: undefined,
    },
    overrides,
  );

export const eventAuthorizationError = (source: 'gosuslugi' | 'sravni', overrides?: TEventOverrides): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Ошибка авторизации',
      eventLabel: [source === 'gosuslugi' ? 'Госуслуги' : 'Сравни', 'Лендинг подбора'],
      eventValue: undefined,
    },
    overrides,
  );

// В eventLabel передаем способ авторизации:
// - Войти Сравни (авторизация Сравни)
// - Войти Госуслуги (авторизация через Госуслуги)
// - Войти Подбор (авторизация через лендинг)
// - Распознан по номеру (в случае автоматической авторизации)
export const eventAuthorizationSuccess = (
  source: 'gosuslugi' | 'sravni' | 'landing' | 'header',
  overrides?: TEventOverrides,
): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Вход в лк',
      eventLabel: {
        gosuslugi: 'Войти Госуслуги',
        sravni: 'Войти Сравни',
        landing: 'Войти Подбор',
        header: 'Распознан по номеру',
      }[source],
      eventValue: undefined,
    },
    overrides,
  );

export const eventCloseSMSPopupClick = (overrides?: TEventOverrides): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Клик',
      eventLabel: 'Закрыть pop-up код из СМС',
      eventValue: undefined,
    },
    overrides,
  );

export const eventShowStep = (step: number, stepName: string, overrides?: TEventOverrides): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: ['Просмотр экрана', `Шаг ${step}`, stepName],
      eventLabel: undefined,
      eventValue: undefined,
    },
    overrides,
  );

export const eventStepCreditPurpose = (
  fillType: 'auto' | 'manual',
  stepData: TStepDataItem[],
  overrides?: TEventOverrides,
): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Шаг 1|Цель кредита',
      eventLabel: [getFillTypeLabel(fillType), ...getStepDataLabels(stepData)],
      eventValue: undefined,
    },
    overrides,
  );

export const eventStepContacts = (
  fillType: 'auto' | 'manual',
  stepData: TStepDataItem[],
  overrides?: TEventOverrides,
): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Шаг 2|Контакты',
      eventLabel: [getFillTypeLabel(fillType), ...getStepDataLabels(stepData)],
      eventValue: undefined,
    },
    overrides,
  );

export const eventStepPassport = (
  fillType: 'auto' | 'manual',
  stepData: TStepDataItem[],
  overrides?: TEventOverrides,
): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Шаг 3|Паспорт',
      eventLabel: [getFillTypeLabel(fillType), ...getStepDataLabels(stepData)],
      eventValue: undefined,
    },
    overrides,
  );

export const eventStepWorkplace = (
  fillType: 'auto' | 'manual',
  stepData: TStepDataItem[],
  overrides?: TEventOverrides,
): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Шаг 4|Работа',
      eventLabel: [getFillTypeLabel(fillType), ...getStepDataLabels(stepData)],
      eventValue: undefined,
    },
    overrides,
  );

export const eventStepAdditionalData = (
  fillType: 'auto' | 'manual',
  stepData: TStepDataItem[],
  overrides?: TEventOverrides,
): IInitialEvent =>
  withEventOverrides(
    {
      event: EVENT_TYPE,
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Шаг 5|Дополнительные данные',
      eventLabel: [getFillTypeLabel(fillType), ...getStepDataLabels(stepData)],
      eventValue: undefined,
    },
    overrides,
  );
